/**
 * Auto-Reminder Service
 *
 * Automatically creates and manages reminders based on expiry/end dates
 * found on insurance policies, personal IDs, vehicle registrations, etc.
 *
 * Preferences are stored on the pilot document + localStorage.
 * AutoReminderRecords live in `profiles/{profileId}/autoReminders/{id}`.
 * Per-item overrides stored in localStorage as `lucidDock_reminderOverrides`.
 */

import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  query,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ProfileService } from './profileService';
import type {
  AutoReminderRecord,
  AutoReminderCategory,
  ReminderPreferences,
  ReminderTimingPreset,
} from '../types/reminder';
import type { Profile } from '../types/profile';
import {
  FOLLOW_UP_DAYS_BEFORE,
  DEFAULT_REMINDER_PREFERENCES,
  getLeadDays,
} from '../constants/reminderDefaults';
import { resolveFrequency } from '../../design-system/patterns/MaintenanceSuggestions';

const PREFS_KEY = 'lucidDock_reminderPrefs';
const OVERRIDES_KEY = 'lucidDock_reminderOverrides';
const OPTED_IN_KEY = 'lucidDock_reminderOptedIn';

export interface ExpirySource {
  id: string;
  category: AutoReminderCategory;
  label: string;
  expiryDate: string;
  frequency?: string;
}

export type ItemOverride = ReminderTimingPreset | 'off';

class AutoReminderService {
  // ── Opt-in State ─────────────────────────────────────────────

  hasOptedIn(): boolean {
    return localStorage.getItem(OPTED_IN_KEY) === 'true';
  }

  setOptedIn(value: boolean): void {
    localStorage.setItem(OPTED_IN_KEY, String(value));
    if (value) {
      const prefs = this.getPreferences();
      prefs.enabled = true;
      this.savePreferences(prefs);
    }
  }

  hasDismissedOptIn(): boolean {
    return localStorage.getItem(OPTED_IN_KEY) === 'dismissed';
  }

  dismissOptIn(): void {
    localStorage.setItem(OPTED_IN_KEY, 'dismissed');
  }

  needsOptInPrompt(): boolean {
    const val = localStorage.getItem(OPTED_IN_KEY);
    return !val;
  }

  // ── Preferences ──────────────────────────────────────────────

  getPreferences(): ReminderPreferences {
    try {
      const stored = localStorage.getItem(PREFS_KEY);
      if (stored) return { ...DEFAULT_REMINDER_PREFERENCES, ...JSON.parse(stored) };
    } catch { /* use defaults */ }
    return { ...DEFAULT_REMINDER_PREFERENCES };
  }

  async savePreferences(prefs: ReminderPreferences): Promise<void> {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    const pilotId = ProfileService.getCurrentUserId();
    if (pilotId) {
      try {
        await ProfileService.updatePilotInfo(pilotId, { reminderPreferences: prefs });
      } catch { /* localStorage is the primary store; pilot doc is backup */ }
    }
  }

  async loadPreferencesFromServer(): Promise<ReminderPreferences> {
    const pilotId = ProfileService.getCurrentUserId();
    if (pilotId) {
      try {
        const pilotInfo = await ProfileService.getPilotInfo(pilotId);
        const pilotData = pilotInfo as unknown as Record<string, unknown> | null;
        if (pilotData?.reminderPreferences) {
          const prefs = pilotData.reminderPreferences as ReminderPreferences;
          localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
          return { ...DEFAULT_REMINDER_PREFERENCES, ...prefs };
        }
      } catch { /* fall through to defaults */ }
    }
    return this.getPreferences();
  }

  // ── Per-item Overrides ───────────────────────────────────────

  getItemOverride(sourceId: string): ItemOverride | null {
    try {
      const stored = localStorage.getItem(OVERRIDES_KEY);
      if (stored) {
        const map = JSON.parse(stored) as Record<string, ItemOverride>;
        return map[sourceId] ?? null;
      }
    } catch { /* ignore */ }
    return null;
  }

  setItemOverride(sourceId: string, value: ItemOverride | null): void {
    try {
      const stored = localStorage.getItem(OVERRIDES_KEY);
      const map: Record<string, ItemOverride> = stored ? JSON.parse(stored) : {};
      if (value === null) {
        delete map[sourceId];
      } else {
        map[sourceId] = value;
      }
      localStorage.setItem(OVERRIDES_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
  }

  private getEffectiveLeadDays(source: ExpirySource, globalTiming: ReminderTimingPreset): number | null {
    const override = this.getItemOverride(source.id);
    if (override === 'off') return null;
    if (override !== null) return getLeadDays(source.category, override as ReminderTimingPreset);
    return getLeadDays(source.category, globalTiming);
  }

  // ── Expiry Date Extraction ───────────────────────────────────

  extractExpirySources(profile: Profile): ExpirySource[] {
    const sources: ExpirySource[] = [];

    for (const policy of profile.insurancePolicies || []) {
      if (policy.renewalDate) {
        sources.push({
          id: `ins-${policy.id}`,
          category: 'insurance',
          label: `${policy.company || 'Insurance'} ${policy.insuranceType || ''}`.trim(),
          expiryDate: policy.renewalDate,
        });
      }
    }

    for (const item of profile.personalItems || []) {
      if (item.expiryDate && !item.doesNotExpire) {
        const cat = this.personalItemCategory(item.type);
        sources.push({
          id: `per-${item.id}`,
          category: cat,
          label: item.label || item.type,
          expiryDate: item.expiryDate,
        });
      }
    }

    for (const vehicle of profile.vehicles || []) {
      if (vehicle.documents) {
        for (const vDoc of vehicle.documents) {
          if (vDoc.expiresAt && (vDoc.type === 'registration' || vDoc.type === 'inspection')) {
            sources.push({
              id: `veh-${vehicle.id}-${vDoc.id}`,
              category: 'vehicle_registration',
              label: `${vehicle.year} ${vehicle.make} ${vehicle.model} — ${vDoc.type}`,
              expiryDate: vDoc.expiresAt,
            });
          }
        }
      }
    }

    for (const addr of profile.addresses || []) {
      const warrantyExpiry = (addr as unknown as Record<string, unknown>).warrantyExpiry as string | undefined;
      if (warrantyExpiry) {
        sources.push({
          id: `adr-warranty-${addr.id}`,
          category: 'warranty',
          label: `${addr.label || addr.street} — Home Warranty`,
          expiryDate: warrantyExpiry,
        });
      }
      for (const d of addr.importantDates || []) {
        sources.push({
          id: `adr-${addr.id}_date_${d.id}`,
          category: 'important_date',
          label: `${addr.label || addr.street} — ${d.label}`,
          expiryDate: d.date,
          frequency: resolveFrequency(d.label, d.frequency),
        });
      }
    }

    for (const vehicle of profile.vehicles || []) {
      for (const d of vehicle.importantDates || []) {
        sources.push({
          id: `veh-${vehicle.id}_date_${d.id}`,
          category: 'important_date',
          label: `${vehicle.year} ${vehicle.make} ${vehicle.model} — ${d.label}`,
          expiryDate: d.date,
          frequency: resolveFrequency(d.label, d.frequency),
        });
      }
    }

    for (const policy of profile.insurancePolicies || []) {
      for (const d of policy.importantDates || []) {
        sources.push({
          id: `ins-${policy.id}_date_${d.id}`,
          category: 'important_date',
          label: `${policy.company || 'Insurance'} — ${d.label}`,
          expiryDate: d.date,
          frequency: resolveFrequency(d.label, d.frequency),
        });
      }
    }

    for (const item of profile.personalItems || []) {
      for (const d of item.importantDates || []) {
        sources.push({
          id: `per-${item.id}_date_${d.id}`,
          category: 'important_date',
          label: `${item.label || item.type} — ${d.label}`,
          expiryDate: d.date,
          frequency: resolveFrequency(d.label, d.frequency),
        });
      }
    }

    return sources;
  }

  personalItemCategory(type: string): AutoReminderCategory {
    switch (type) {
      case 'passport':
      case 'id_license':
      case 'state_id':
      case 'student_id':
        return 'id_document';
      case 'health_insurance':
      case 'dental_insurance':
      case 'vision_insurance':
      case 'doctor':
      case 'dentist':
      case 'pharmacy':
        return 'health';
      case 'bank_account':
      case 'credit_card':
        return 'financial';
      case 'membership':
      case 'gym':
        return 'membership';
      default:
        return 'membership';
    }
  }

  // ── Date Calculations ────────────────────────────────────────

  calculateNotificationDate(expiryDate: string, leadDays: number): string {
    const d = new Date(expiryDate);
    d.setDate(d.getDate() - leadDays);
    return d.toISOString().split('T')[0];
  }

  private calculateFollowUpDate(expiryDate: string): string {
    const d = new Date(expiryDate);
    d.setDate(d.getDate() - FOLLOW_UP_DAYS_BEFORE);
    return d.toISOString().split('T')[0];
  }

  formatReminderDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ── Sync Engine ──────────────────────────────────────────────

  async syncReminders(profileId: string, profile: Profile): Promise<void> {
    const prefs = this.getPreferences();
    if (!prefs.enabled) return;

    const sources = this.extractExpirySources(profile);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayStr = now.toISOString().split('T')[0];

    const futureSources = sources.filter(s => s.expiryDate >= todayStr);

    const existingRef = collection(db, 'profiles', profileId, 'autoReminders');
    const existingSnap = await getDocs(query(existingRef));
    const existingMap = new Map<string, AutoReminderRecord>();
    existingSnap.forEach(d => existingMap.set(d.id, d.data() as AutoReminderRecord));

    const desiredIds = new Set<string>();
    const batch = writeBatch(db);
    let batchCount = 0;

    for (const source of futureSources) {
      const leadDays = this.getEffectiveLeadDays(source, prefs.timing);
      if (leadDays === null) continue;

      desiredIds.add(source.id);
      const nextNotificationAt = this.calculateNotificationDate(source.expiryDate, leadDays);
      const followUpAt = this.calculateFollowUpDate(source.expiryDate);
      const existing = existingMap.get(source.id);

      if (existing && existing.expiryDate === source.expiryDate && existing.leadDays === leadDays) {
        continue;
      }

      const record: AutoReminderRecord = {
        id: source.id,
        profileId,
        category: source.category,
        sourceId: source.id,
        sourceLabel: source.label,
        expiryDate: source.expiryDate,
        leadDays,
        nextNotificationAt,
        followUpAt,
        status: nextNotificationAt <= todayStr ? 'notified' : 'scheduled',
        createdAt: existing?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      batch.set(doc(existingRef, source.id), record);
      batchCount++;
    }

    for (const [id] of existingMap) {
      if (!desiredIds.has(id)) {
        batch.delete(doc(existingRef, id));
        batchCount++;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }
  }

  async getAutoReminders(profileId: string): Promise<AutoReminderRecord[]> {
    const ref = collection(db, 'profiles', profileId, 'autoReminders');
    const snap = await getDocs(query(ref));
    const records: AutoReminderRecord[] = [];
    snap.forEach(d => records.push(d.data() as AutoReminderRecord));
    return records.sort((a, b) => a.nextNotificationAt.localeCompare(b.nextNotificationAt));
  }

  getUpcomingFromSources(profile: Profile): { source: ExpirySource; leadDays: number; reminderDate: string }[] {
    const prefs = this.getPreferences();
    if (!prefs.enabled) return [];

    const sources = this.extractExpirySources(profile);
    const todayStr = new Date().toISOString().split('T')[0];

    return sources
      .filter(s => s.expiryDate >= todayStr)
      .map(s => {
        const leadDays = this.getEffectiveLeadDays(s, prefs.timing);
        if (leadDays === null) return null;
        return {
          source: s,
          leadDays,
          reminderDate: this.calculateNotificationDate(s.expiryDate, leadDays),
        };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => a.reminderDate.localeCompare(b.reminderDate));
  }

  async deleteAutoReminder(profileId: string, reminderId: string): Promise<void> {
    await deleteDoc(doc(db, 'profiles', profileId, 'autoReminders', reminderId));
  }

  async recalculateAll(profileId: string, profile: Profile): Promise<void> {
    const ref = collection(db, 'profiles', profileId, 'autoReminders');
    const snap = await getDocs(query(ref));
    const batch = writeBatch(db);
    snap.forEach(d => batch.delete(d.ref));
    if (!snap.empty) await batch.commit();
    await this.syncReminders(profileId, profile);
  }
}

export const autoReminderService = new AutoReminderService();
